import { useState, useEffect } from 'react'
import axios from 'axios'
import Header from '../components/shared/Header'
import Footer from '../components/shared/Footer'

export default function StatsPage() {
  const [authenticated, setAuthenticated] = useState(false)
  const [password, setPassword] = useState('')
  const [token, setToken] = useState('')
  const [error, setError] = useState('')
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(false)

  // Check if token is stored in sessionStorage
  useEffect(() => {
    const savedToken = sessionStorage.getItem('stats_token')
    if (savedToken) {
      setToken(savedToken)
      setAuthenticated(true)
      // Try to load stats immediately
      loadStats(savedToken)
    }
  }, [])

  // Helper function to create Bearer token header
  const getAuthHeader = (authToken) => {
    return `Bearer ${authToken}`
  }

  const handleLogin = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      // First, login to get JWT token
      // Note: Username is configured on backend, could be made configurable via env
      const loginResponse = await axios.post('/api/v1/auth/login', {
        username: 'admin', // TODO: Make configurable via environment variable
        password: password
      }, {
        timeout: 10000
      })
      
      const authToken = loginResponse.data.access_token
      setToken(authToken)
      setAuthenticated(true)
      // Store token in sessionStorage for this browser session
      sessionStorage.setItem('stats_token', authToken)
      setPassword('')
      
      // Now load stats with the token
      await loadStats(authToken)
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Invalid password')
        sessionStorage.removeItem('stats_token')
      } else {
        setError('Error logging in: ' + (err.message || 'Unknown error'))
        console.error('Login error:', err)
      }
    } finally {
      setLoading(false)
    }
  }

  const loadStats = async (authToken = token) => {
    if (!authToken) return
    
    try {
      const response = await axios.get('/api/v1/stats', {
        headers: {
          'Authorization': getAuthHeader(authToken)
        },
        timeout: 10000
      })
      setStats(response.data)
      setError('')
    } catch (err) {
      if (err.response?.status === 401) {
        setAuthenticated(false)
        setToken('')
        setPassword('')
        setStats(null)
        sessionStorage.removeItem('stats_token')
        setError('Session expired. Please login again.')
      } else {
        console.error('Error loading stats:', err)
        // Don't show error for network issues, just log it
      }
    }
  }

  const handleLogout = () => {
    setAuthenticated(false)
    setToken('')
    setPassword('')
    setStats(null)
    sessionStorage.removeItem('stats_token')
  }

  useEffect(() => {
    if (authenticated && token) {
      loadStats(token)
      // Refresh every 30 seconds
      const interval = setInterval(() => loadStats(token), 30000)
      return () => clearInterval(interval)
    }
  }, [authenticated, token])

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-bg-dark text-text-dark">
        <div className="max-w-container mx-auto px-5 py-8">
          <Header />
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="w-full max-w-md">
              <div className="bg-surface-dark-1 rounded-lg p-8 border border-border-dark">
                <h1 className="text-2xl font-semibold mb-6">Stats Dashboard</h1>
                <form onSubmit={handleLogin}>
                  <div className="mb-4">
                    <label htmlFor="password" className="block text-sm font-medium mb-2">
                      Admin Password
                    </label>
                    <input
                      type="password"
                      id="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full px-4 py-2 bg-surface-dark-2 border border-border-dark rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-dark"
                      placeholder="Enter admin password"
                      autoFocus
                      required
                    />
                    <p className="mt-2 text-xs text-muted-dark">
                      Uses JWT authentication - token expires after 24 hours
                    </p>
                  </div>
                  {error && (
                    <div className="mb-4 text-red-400 text-sm">{error}</div>
                  )}
                  <button
                    type="submit"
                    disabled={loading || !password}
                    className="w-full px-4 py-2 bg-accent-dark text-white rounded-lg hover:bg-accent-dark/90 disabled:opacity-50"
                  >
                    {loading ? 'Logging in...' : 'Access Stats'}
                  </button>
                </form>
              </div>
            </div>
          </div>
          <Footer />
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-bg-dark text-text-dark">
      <div className="max-w-container mx-auto px-5 py-8">
        <Header />
        
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold mb-2">Request Statistics</h1>
            <p className="text-muted-dark">Real-time logging and analytics</p>
          </div>
          <button
            onClick={handleLogout}
            className="px-4 py-2 bg-surface-dark-2 border border-border-dark rounded-lg hover:bg-surface-dark-3 text-sm"
          >
            Logout
          </button>
        </div>

        {stats && (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-surface-dark-1 rounded-lg p-6 border border-border-dark">
                <div className="text-muted-dark text-sm mb-1">Total Logs</div>
                <div className="text-3xl font-bold">{stats.summary?.total_logs || 0}</div>
              </div>
              <div className="bg-surface-dark-1 rounded-lg p-6 border border-border-dark">
                <div className="text-muted-dark text-sm mb-1">Last 24 Hours</div>
                <div className="text-3xl font-bold">{stats.summary?.logs_last_24h || 0}</div>
              </div>
              <div className="bg-surface-dark-1 rounded-lg p-6 border border-border-dark">
                <div className="text-muted-dark text-sm mb-1">Last Hour</div>
                <div className="text-3xl font-bold">{stats.summary?.logs_last_hour || 0}</div>
              </div>
            </div>

            {/* Method & Status Distribution */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-surface-dark-1 rounded-lg p-6 border border-border-dark">
                <h2 className="text-xl font-semibold mb-4">HTTP Methods</h2>
                <div className="space-y-2">
                  {Object.entries(stats.method_distribution || {}).map(([method, count]) => (
                    <div key={method} className="flex justify-between items-center">
                      <span className="font-mono text-sm">{method}</span>
                      <span className="text-accent-dark font-semibold">{count}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-surface-dark-1 rounded-lg p-6 border border-border-dark">
                <h2 className="text-xl font-semibold mb-4">Status Codes</h2>
                <div className="space-y-2">
                  {Object.entries(stats.status_code_distribution || {}).map(([code, count]) => (
                    <div key={code} className="flex justify-between items-center">
                      <span className={`font-mono text-sm ${code.startsWith('2') ? 'text-green-400' : code.startsWith('4') ? 'text-yellow-400' : 'text-red-400'}`}>
                        {code}
                      </span>
                      <span className="text-accent-dark font-semibold">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Top IPs */}
            <div className="bg-surface-dark-1 rounded-lg p-6 border border-border-dark">
              <h2 className="text-xl font-semibold mb-4">Top IPs (Last 24 Hours)</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border-dark">
                      <th className="text-left py-2">IP Address</th>
                      <th className="text-right py-2">Requests</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.top_ips_24h?.map((item, idx) => (
                      <tr key={idx} className="border-b border-border-dark/50">
                        <td className="py-2 font-mono">{item.ip}</td>
                        <td className="py-2 text-right text-accent-dark font-semibold">{item.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Browser & OS Distribution */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-surface-dark-1 rounded-lg p-6 border border-border-dark">
                <h2 className="text-xl font-semibold mb-4">Top Browsers</h2>
                <div className="space-y-2">
                  {stats.browser_distribution?.map((item, idx) => (
                    <div key={idx} className="flex justify-between items-center">
                      <span className="text-sm">{item.browser}</span>
                      <span className="text-accent-dark font-semibold">{item.count}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-surface-dark-1 rounded-lg p-6 border border-border-dark">
                <h2 className="text-xl font-semibold mb-4">Operating Systems</h2>
                <div className="space-y-2">
                  {stats.os_distribution?.map((item, idx) => (
                    <div key={idx} className="flex justify-between items-center">
                      <span className="text-sm">{item.os}</span>
                      <span className="text-accent-dark font-semibold">{item.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Device Types */}
            {stats.device_types && (
              <div className="bg-surface-dark-1 rounded-lg p-6 border border-border-dark">
                <h2 className="text-xl font-semibold mb-4">Device Types</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <div className="text-muted-dark text-sm">📱 Mobile</div>
                    <div className="text-2xl font-bold">{stats.device_types.mobile}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-muted-dark text-sm">💻 Desktop</div>
                    <div className="text-2xl font-bold">{stats.device_types.desktop}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-muted-dark text-sm">📟 Tablet</div>
                    <div className="text-2xl font-bold">{stats.device_types.tablet}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-muted-dark text-sm">🤖 Bots</div>
                    <div className="text-2xl font-bold">{stats.device_types.bot}</div>
                  </div>
                </div>
              </div>
            )}

            {/* Geographic Distribution */}
            {stats.top_countries && stats.top_countries.length > 0 && (
              <div className="bg-surface-dark-1 rounded-lg p-6 border border-border-dark">
                <h2 className="text-xl font-semibold mb-4">🌍 Top Countries</h2>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border-dark">
                        <th className="text-left py-2">Country</th>
                        <th className="text-right py-2">Requests</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.top_countries.map((item, idx) => (
                        <tr key={idx} className="border-b border-border-dark/50">
                          <td className="py-2">{item.country} - {item.country_name}</td>
                          <td className="py-2 text-right text-accent-dark font-semibold">{item.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Top Cities */}
            {stats.top_cities && stats.top_cities.length > 0 && (
              <div className="bg-surface-dark-1 rounded-lg p-6 border border-border-dark">
                <h2 className="text-xl font-semibold mb-4">🏙️ Top Cities</h2>
                <div className="space-y-2">
                  {stats.top_cities.map((item, idx) => (
                    <div key={idx} className="flex justify-between items-center py-2 border-b border-border-dark/30">
                      <span className="text-sm">{item.city}</span>
                      <span className="text-accent-dark font-semibold">{item.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* UTM Sources */}
            {stats.utm_sources && stats.utm_sources.length > 0 && (
              <div className="bg-surface-dark-1 rounded-lg p-6 border border-border-dark">
                <h2 className="text-xl font-semibold mb-4">📊 Traffic Sources (UTM)</h2>
                <div className="space-y-2">
                  {stats.utm_sources.map((item, idx) => (
                    <div key={idx} className="flex justify-between items-center py-2 border-b border-border-dark/30">
                      <span className="text-sm">{item.source}</span>
                      <span className="text-accent-dark font-semibold">{item.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Top Paths */}
            <div className="bg-surface-dark-1 rounded-lg p-6 border border-border-dark">
              <h2 className="text-xl font-semibold mb-4">Most Requested Paths</h2>
              <div className="space-y-2">
                {stats.top_paths?.slice(0, 10).map((item, idx) => (
                  <div key={idx} className="flex justify-between items-center py-2 border-b border-border-dark/30">
                    <span className="font-mono text-sm text-muted-dark">{item.path}</span>
                    <span className="text-accent-dark font-semibold">{item.count}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Response Time Stats */}
            {stats.response_time_stats && (
              <div className="bg-surface-dark-1 rounded-lg p-6 border border-border-dark">
                <h2 className="text-xl font-semibold mb-4">Response Time Statistics</h2>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <div className="text-muted-dark text-sm">Average</div>
                    <div className="text-2xl font-bold">{stats.response_time_stats.avg_ms}ms</div>
                  </div>
                  <div>
                    <div className="text-muted-dark text-sm">Min</div>
                    <div className="text-2xl font-bold">{stats.response_time_stats.min_ms}ms</div>
                  </div>
                  <div>
                    <div className="text-muted-dark text-sm">Max</div>
                    <div className="text-2xl font-bold">{stats.response_time_stats.max_ms}ms</div>
                  </div>
                </div>
              </div>
            )}

            {/* Recent Activity - Enhanced */}
            <div className="bg-surface-dark-1 rounded-lg p-6 border border-border-dark">
              <h2 className="text-xl font-semibold mb-4">Recent Activity</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-border-dark">
                      <th className="text-left py-2">Time</th>
                      <th className="text-left py-2">Path</th>
                      <th className="text-left py-2">IP</th>
                      <th className="text-left py-2">Browser</th>
                      <th className="text-left py-2">OS</th>
                      <th className="text-left py-2">Device</th>
                      <th className="text-left py-2">Location</th>
                      <th className="text-right py-2">Status</th>
                      <th className="text-right py-2">ms</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.recent_activity?.map((log, idx) => (
                      <tr key={idx} className="border-b border-border-dark/30">
                        <td className="py-2 text-muted-dark whitespace-nowrap">
                          {log.timestamp ? new Date(log.timestamp).toLocaleTimeString() : '-'}
                        </td>
                        <td className="py-2 font-mono text-muted-dark max-w-[200px] truncate" title={log.path}>
                          {log.path}
                        </td>
                        <td className="py-2 font-mono text-muted-dark">{log.client_ip}</td>
                        <td className="py-2 text-muted-dark">
                          {log.browser_name ? `${log.browser_name} ${log.browser_version || ''}`.trim() : '-'}
                        </td>
                        <td className="py-2 text-muted-dark">{log.os_name || '-'}</td>
                        <td className="py-2 text-center">
                          {log.is_mobile ? '📱' : log.is_tablet ? '📟' : log.is_bot ? '🤖' : '💻'}
                        </td>
                        <td className="py-2 text-muted-dark">
                          {log.geo_city ? `${log.geo_city}, ${log.geo_country}` : log.geo_country || '-'}
                        </td>
                        <td className={`py-2 text-right ${
                          log.status_code >= 200 && log.status_code < 300 ? 'text-green-400' :
                          log.status_code >= 400 ? 'text-red-400' : 'text-yellow-400'
                        }`}>
                          {log.status_code}
                        </td>
                        <td className="py-2 text-right text-muted-dark">{log.response_time_ms?.toFixed(1)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        <Footer />
      </div>
    </div>
  )
}

